import { computed, inject, Injectable, signal } from '@angular/core';
import { ProductsStore } from '../../pages/covers-page/store/products.store';
import { TravelersStore } from '../../core/stores/travelers.store';
import { IBenefitCategory, ITravelersSelections } from '../models/benefit.interface';
import { ICover } from '../models/cover.interface';

@Injectable({
  providedIn: 'root'
})
export class CoverService {
  private productStore = inject(ProductsStore);
  private travelersStore = inject(TravelersStore);
  private selectedProductID = computed(() => this.productStore.selectedProductId());
  private coverDescriptions = signal(new Set<{ id: number; description: string }>());

  // Adds cover individually
  public addCover(coverID: number, travelerID: string): void {
    const selectedProductID = this.selectedProductID();
    if (!selectedProductID) return;

    const productCover = this.productStore.selectedProduct();
    if (!productCover) return;

    if (travelerID) this.travelersStore.addCoverToTraveler(selectedProductID, coverID, travelerID);
  }

  public addCoverToAll(coverID: number): void {
    const selectedProductID = this.selectedProductID();
    if (!selectedProductID) return;

    const productCover = this.productStore.selectedProduct();
    if (!productCover) return;

    this.travelersStore.addCoverToAllTravelers(selectedProductID, coverID);
  }

  // Removes cover individually
  public removeCover(coverID: number, travelerID: string): void {
    const selectedProductID = this.selectedProductID();
    if (!selectedProductID) return;

    const productCover = this.productStore.selectedProduct();
    if (!productCover) return;

    const coverLevelID = this.getLevelCoverFromAddedParentCover(coverID);

    if (coverLevelID && travelerID) {
      this.travelersStore.removeCoverFromTraveler(selectedProductID, coverLevelID, travelerID);
    } else {
      if (travelerID) this.travelersStore.removeCoverFromTraveler(selectedProductID, coverID, travelerID);
    }
  }

  public removeCoverFromAll(coverID: number): void {
    const selectedProductID = this.selectedProductID();
    if (!selectedProductID) return;

    const productCover = this.productStore.selectedProduct();
    if (!productCover) return;

    const coverLevelID = this.getLevelCoverFromAddedParentCover(coverID);

    if (coverLevelID) {
      this.travelersStore.removeCoverFromAllTravelers(selectedProductID, coverLevelID);
    } else {
      this.travelersStore.removeCoverFromAllTravelers(selectedProductID, coverID);
    }
  }

  // Checks if travelers needs to be toggled individually
  public areTravelersAutomaticallyCovered(coverID: number): boolean | undefined {
    const cover = this.productStore.allCovers().find((cover) => cover.id === coverID);
    return cover?.isMustAll || cover?.onlyOneMember;
  }

  // Updates the traveler selections if the cover is picked
  public updateAndGetTravelersCoversSelections(coverID: number): ITravelersSelections[] | null {
    const travelersSelections: ITravelersSelections[] = [];
    const travelers = this.travelersStore.allTravelers();
    if (!travelers) return null;

    const selectedProductID = this.productStore.selectedProductId();
    if (!selectedProductID) return null;

    const coverLevelID = this.getLevelCoverFromAddedParentCover(coverID);

    travelers.forEach((traveler) => {
      const coversIDList = traveler.productCover?.find((product) => product.productID === selectedProductID)?.coversIds;

      if (coversIDList?.includes(coverID) || coverLevelID) {
        travelersSelections.push({ id: traveler.id, selected: true });
      } else {
        travelersSelections.push({ id: traveler.id, selected: false });
      }
    });
    return travelersSelections;
  }

  // Gathers and returns all cover IDs associated with the selected product across all travelers
  public pickedCovers(): number[] {
    const travelers = this.travelersStore.allTravelers();
    if (!travelers) return [];

    const selectedProductID = this.productStore.selectedProductId();
    if (!selectedProductID) return [];

    return travelers.flatMap(
      (traveler) => traveler.productCover.find((product) => product.productID === selectedProductID)?.coversIds ?? [0]
    );
  }

  public getLevelCoverFromAddedParentCover(parentCoverID: number): number | undefined {
    const parentCover = this.productStore.allCovers().find((cover) => cover.id === parentCoverID);
    if (!parentCover) return undefined;

    const pickedCoversSet = new Set(this.pickedCovers());
    const matchingLevel = parentCover.levels?.find((level) => pickedCoversSet.has(level.benefitId));

    return matchingLevel?.benefitId;
  }

  public getBenefitCategoryFromParentBenefit(parentBenefitID: number): IBenefitCategory | undefined {
    const parentBenefit = this.productStore.benefits().find((benefit) => benefit.id === parentBenefitID);
    if (!parentBenefit) return undefined;

    const pickedBenefitSet = new Set(this.pickedCovers());
    const matchingLevel = parentBenefit.categories.find((category) => pickedBenefitSet.has(category.id));

    return matchingLevel;
  }

  public updateCoverDescription(id: number, description: string): void {
    this.coverDescriptions.update((currentSet) => {
      // Convert the set to an array for iteration, then remove items with the matching id
      [...currentSet].forEach((item) => {
        if (item.id === id) {
          currentSet.delete(item);
        }
      });
      // Add the updated or new item
      currentSet.add({ id, description });
      return currentSet;
    });
  }

  public getCategoryDescriptionByCoverID(coverID: number): string | undefined {
    const descriptions = this.coverDescriptions();
    for (const entry of descriptions) {
      if (entry.id === coverID) {
        return entry.description;
      }
    }
    return undefined;
  }

  public getParentCoverFromAddedCoverLevel(coverLevelID: number): ICover | undefined {
    const parentCover = this.productStore
      .allCovers()
      .find((cover) => cover.levels?.some((level) => level.benefitId === coverLevelID));

    return parentCover;
  }
}
